
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { AiAnalysisSession } from '@/services/AiMarketAnalysisService';

export class MarketAnalysisExportService {
  private static addWatermark(doc: jsPDF) {
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    
    // Add subtle watermark at bottom
    doc.setFontSize(8);
    doc.setTextColor(200, 200, 200);
    doc.text('Trade Bot 365', pageWidth - 25, pageHeight - 10);
    
    // Add header logo area
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('Trade Bot 365', 14, 10);
  }

  private static addAnalysisHeader(doc: jsPDF, session: AiAnalysisSession, startY: number): number {
    let yPos = startY;
    
    // Title
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text(`${session.symbol} Analysis Report`, 14, yPos);
    yPos += 10;
    
    // Basic info
    doc.setFontSize(12);
    const date = new Date(session.created_at).toLocaleDateString();
    const time = new Date(session.created_at).toLocaleTimeString();
    
    doc.text(`Date: ${date} at ${time}`, 14, yPos);
    yPos += 6;
    doc.text(`Status: ${session.status}`, 14, yPos);
    yPos += 6;
    doc.text(`Progress: ${session.progress_percentage}%`, 14, yPos);
    yPos += 10;
    
    return yPos;
  }

  private static addAnalysisCards(doc: jsPDF, session: AiAnalysisSession, startY: number): number {
    let yPos = startY;
    
    // Key Levels
    if (session.card_results?.key_levels?.status === 'completed') {
      yPos = this.addKeyLevelsSection(doc, session.card_results.key_levels.data, yPos);
    }
    
    // Price Targets
    if (session.card_results?.price_targets?.status === 'completed') {
      yPos = this.addPriceTargetsSection(doc, session.card_results.price_targets.data, yPos);
    }
    
    // Trends Analysis
    if (session.card_results?.trends_analysis?.status === 'completed') {
      yPos = this.addTrendsSection(doc, session.card_results.trends_analysis.data, yPos);
    }
    
    // AI Recommendation
    if (session.card_results?.ai_recommendation?.status === 'completed') {
      yPos = this.addAiRecommendationSection(doc, session.card_results.ai_recommendation.data, yPos);
    }
    
    return yPos;
  }

  private static addKeyLevelsSection(doc: jsPDF, data: any, startY: number): number {
    let yPos = startY;
    
    // Check if we need a new page
    if (yPos > 250) {
      doc.addPage();
      this.addWatermark(doc);
      yPos = 20;
    }
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Key Levels', 14, yPos);
    yPos += 8;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    
    if (data.resistance && data.resistance.length > 0) {
      doc.text('Resistance Levels:', 14, yPos);
      yPos += 6;
      data.resistance.forEach((level: any, index: number) => {
        doc.text(`  ${index + 1}. ${level.price} (${level.strength})`, 20, yPos);
        yPos += 5;
      });
      yPos += 3;
    }
    
    if (data.support && data.support.length > 0) {
      doc.text('Support Levels:', 14, yPos);
      yPos += 6;
      data.support.forEach((level: any, index: number) => {
        doc.text(`  ${index + 1}. ${level.price} (${level.strength})`, 20, yPos);
        yPos += 5;
      });
      yPos += 10;
    }
    
    return yPos;
  }

  private static addPriceTargetsSection(doc: jsPDF, data: any, startY: number): number {
    let yPos = startY;
    
    if (yPos > 250) {
      doc.addPage();
      this.addWatermark(doc);
      yPos = 20;
    }
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Price Targets', 14, yPos);
    yPos += 8;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    
    const targets = data.priceTargets || data;
    if (targets.bullish) {
      doc.text(`Bullish Target: ${targets.bullish}`, 14, yPos);
      yPos += 6;
    }
    
    if (targets.bearish) {
      doc.text(`Bearish Target: ${targets.bearish}`, 14, yPos);
      yPos += 6;
    }
    
    if (data.confidence) {
      doc.text(`Confidence: ${data.confidence}%`, 14, yPos);
      yPos += 10;
    }
    
    return yPos;
  }

  private static addTrendsSection(doc: jsPDF, data: any, startY: number): number {
    let yPos = startY;
    
    if (yPos > 250) {
      doc.addPage();
      this.addWatermark(doc);
      yPos = 20;
    }
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Trends Analysis', 14, yPos);
    yPos += 8;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    
    if (data.shortTerm) {
      doc.text(`Short Term: ${data.shortTerm.trend} (${data.shortTerm.strength})`, 14, yPos);
      yPos += 6;
    }
    
    if (data.mediumTerm) {
      doc.text(`Medium Term: ${data.mediumTerm.trend} (${data.mediumTerm.strength})`, 14, yPos);
      yPos += 6;
    }
    
    if (data.longTerm) {
      doc.text(`Long Term: ${data.longTerm.trend} (${data.longTerm.strength})`, 14, yPos);
      yPos += 10;
    }
    
    return yPos;
  }

  private static addAiRecommendationSection(doc: jsPDF, data: any, startY: number): number {
    let yPos = startY;
    
    if (yPos > 250) {
      doc.addPage();
      this.addWatermark(doc);
      yPos = 20;
    }
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('AI Recommendation', 14, yPos);
    yPos += 8;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    
    if (data.action) {
      doc.text(`Action: ${data.action}`, 14, yPos);
      yPos += 6;
    }
    
    if (data.confidence) {
      doc.text(`Confidence: ${data.confidence}%`, 14, yPos);
      yPos += 6;
    }
    
    if (data.reasoning) {
      doc.text('Reasoning:', 14, yPos);
      yPos += 6;
      const reasoning = doc.splitTextToSize(data.reasoning, 180);
      doc.text(reasoning, 20, yPos);
      yPos += reasoning.length * 5 + 10;
    }
    
    return yPos;
  }

  static async exportAnalysisToPDF(session: AiAnalysisSession): Promise<void> {
    const doc = new jsPDF();
    
    // Add watermark to first page
    this.addWatermark(doc);
    
    let yPos = 20;
    
    // Add header
    yPos = this.addAnalysisHeader(doc, session, yPos);
    
    // Add analysis cards
    this.addAnalysisCards(doc, session, yPos);
    
    // Download the PDF
    const fileName = `${session.symbol}_Analysis_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  }

  static async exportMultipleAnalyses(sessions: AiAnalysisSession[]): Promise<void> {
    const doc = new jsPDF();
    
    sessions.forEach((session, index) => {
      if (index > 0) {
        doc.addPage();
      }
      
      this.addWatermark(doc);
      let yPos = 20;
      yPos = this.addAnalysisHeader(doc, session, yPos);
      this.addAnalysisCards(doc, session, yPos);
    });
    
    const fileName = `Market_Analysis_Export_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  }
}
